import { useState, useEffect, useRef } from 'react';

const testimonials = [
  {
    name: 'Sarah M.',
    location: 'Lubbock, TX',
    text: 'I had been stuck in the same place for years — same job, same fears, same excuses. After just a few sessions, I finally had a clear plan and the confidence to actually follow through. I cannot believe how much has changed.',
    stars: 5,
    highlight: 'Finally had a clear plan',
    initial: 'S',
  },
  {
    name: 'James R.',
    location: 'Lubbock, TX',
    text: 'Honestly, I was skeptical at first. I thought coaching was just people telling you to \"believe in yourself.\" It is so much more than that. We dug into the real stuff — the habits, the mindset, the blocks. I left every session with something actionable.',
    stars: 5,
    highlight: 'Something actionable every session',
    initial: 'J',
  },
  {
    name: 'Priya K.',
    location: 'Amarillo, TX',
    text: 'I drove in from Amarillo because a friend in Lubbock would not stop talking about how much this coaching changed her life. Now I understand why. The clarity I gained in the first consultation alone was worth the trip.',
    stars: 5,
    highlight: 'Worth every mile',
    initial: 'P',
  },
  {
    name: 'Marcus T.',
    location: 'Lubbock, TX',
    text: 'I was overwhelmed trying to juggle my career goals, my family, and my own personal growth. Brand New You helped me stop spinning my wheels and actually prioritize what matters. I feel like a completely different person.',
    stars: 5,
    highlight: 'Completely different person',
    initial: 'M',
  },
  {
    name: 'Dana W.',
    location: 'Lubbock, TX',
    text: 'The free consultation was no-pressure and incredibly insightful. I walked in thinking I knew what my problem was. Turns out, I had been looking at it all wrong. That shift in perspective changed everything for me.',
    stars: 5,
    highlight: 'No pressure, incredibly insightful',
    initial: 'D',
  },
  {
    name: 'Carlos E.',
    location: 'Midland, TX',
    text: 'I have always had big dreams but could never figure out why I kept self-sabotaging. The coaching process helped me understand myself better and build real momentum. My goals no longer feel out of reach.',
    stars: 5,
    highlight: 'Goals no longer feel out of reach',
    initial: 'C',
  },
];

function StarRating({ count }: { count: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="w-4 h-4 flex items-center justify-center">
          <i className="ri-star-fill text-[#D4896A] text-sm" />
        </div>
      ))}
    </div>
  );
}

export default function TestimonialsSection() {
  const [visible, setVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // Auto-advance
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const prev = () => setActiveIndex((i) => (i - 1 + testimonials.length) % testimonials.length);
  const next = () => setActiveIndex((i) => (i + 1) % testimonials.length);

  const featured = testimonials[activeIndex];

  return (
    <section id="testimonials" className="bg-[#FAF8F5] py-24 md:py-32" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <span
            className="text-xs font-semibold tracking-widest uppercase text-[#D4896A] block mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Real Stories
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-[#2C2C2C] mb-4"
            style={{ fontFamily: '\'Playfair Display\', serif' }}
          >
            Lives Changed, One Step at a Time
          </h2>
          <p
            className="text-lg text-[#6B6B6B] max-w-xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Hear from real people who decided to take that first step toward their Brand New You.
          </p>
        </div>

        {/* Featured Quote — large card */}
        <div
          className={`mb-12 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="bg-[#2D3E2E] rounded-3xl px-8 md:px-16 py-12 md:py-16 relative overflow-hidden">
            {/* Decorative quote mark */}
            <div
              className="absolute top-6 left-8 text-[10rem] leading-none text-white/5 select-none pointer-events-none"
              style={{ fontFamily: '\'Playfair Display\', serif' }}
              aria-hidden="true"
            >
              &ldquo;
            </div>

            <div className="relative z-10 max-w-4xl mx-auto text-center">
              <StarRating count={featured.stars} />
              <p
                className="text-xl md:text-2xl text-white/90 leading-relaxed my-8 italic"
                style={{ fontFamily: '\'Playfair Display\', serif' }}
                key={activeIndex}
              >
                &ldquo;{featured.text}&rdquo;
              </p>
              <div className="flex items-center justify-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#D4896A] flex items-center justify-center shrink-0">
                  <span
                    className="text-white text-lg font-bold"
                    style={{ fontFamily: '\'Playfair Display\', serif' }}
                  >
                    {featured.initial}
                  </span>
                </div>
                <div className="text-left">
                  <p className="text-white font-semibold text-base" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {featured.name}
                  </p>
                  <p className="text-[#A8B5A0] text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {featured.location}
                  </p>
                </div>
              </div>

              {/* Highlight badge */}
              <div className="mt-6 inline-block bg-[#D4896A]/20 border border-[#D4896A]/40 text-[#E8C4A8] text-xs font-semibold uppercase tracking-widest px-5 py-2 rounded-full">
                {featured.highlight}
              </div>
            </div>

            {/* Prev / Next */}
            <div className="flex items-center justify-center gap-4 mt-10">
              <button
                onClick={prev}
                className="w-10 h-10 flex items-center justify-center rounded-full border border-white/20 text-white/70 hover:border-[#D4896A] hover:text-[#D4896A] transition-colors cursor-pointer"
                aria-label="Previous testimonial"
              >
                <i className="ri-arrow-left-s-line text-xl" />
              </button>

              {/* Dots */}
              <div className="flex items-center gap-2">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIndex(i)}
                    className={`rounded-full transition-all duration-300 cursor-pointer ${
                      i === activeIndex
                        ? 'w-6 h-2 bg-[#D4896A]'
                        : 'w-2 h-2 bg-white/30 hover:bg-white/60'
                    }`}
                    aria-label={`Go to testimonial ${i + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={next}
                className="w-10 h-10 flex items-center justify-center rounded-full border border-white/20 text-white/70 hover:border-[#D4896A] hover:text-[#D4896A] transition-colors cursor-pointer"
                aria-label="Next testimonial"
              >
                <i className="ri-arrow-right-s-line text-xl" />
              </button>
            </div>
          </div>
        </div>

        {/* Small Cards Grid */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {testimonials.map((t, i) => (
            <button
              key={t.name}
              onClick={() => setActiveIndex(i)}
              className={`text-left rounded-2xl p-6 transition-all duration-300 cursor-pointer border-2 ${
                i === activeIndex
                  ? 'bg-white border-[#D4896A] shadow-sm'
                  : 'bg-white border-transparent hover:border-[#E0D0C0]'
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${i === activeIndex ? 'bg-[#D4896A]' : 'bg-[#EDE8E0]'}`}>
                  <span
                    className={`text-sm font-bold ${i === activeIndex ? 'text-white' : 'text-[#8A7A6A]'}`}
                    style={{ fontFamily: '\'Playfair Display\', serif' }}
                  >
                    {t.initial}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#2C2C2C]" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {t.name}
                  </p>
                  <p className="text-xs text-[#8A7A6A]" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {t.location}
                  </p>
                </div>
                <div className="ml-auto flex items-center gap-0.5">
                  {Array.from({ length: t.stars }).map((_, si) => (
                    <div key={si} className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-star-fill text-[#D4896A] text-xs" />
                    </div>
                  ))}
                </div>
              </div>
              <p
                className="text-sm text-[#6B6B6B] leading-relaxed line-clamp-3"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                &ldquo;{t.text}&rdquo;
              </p>
              {i === activeIndex && (
                <div className="mt-3">
                  <span className="text-xs font-semibold text-[#D4896A] uppercase tracking-wide">
                    {t.highlight}
                  </span>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
